import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  MessageSquare,
  Send,
  Search,
  Phone,
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  MapPin,
  Clock,
} from "lucide-react";


export default function SmsManagementPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchPhone, setSearchPhone] = useState("");
  const [directionFilter, setDirectionFilter] = useState("");
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sendMessage, setSendMessage] = useState("");
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem("token");

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (searchPhone) params.append("phone_number", searchPhone);
      if (directionFilter) params.append("direction", directionFilter);

      const response = await axios.get(`${API_URL}/sms/messages?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data);
    } catch (error) {
      toast.error("Failed to load SMS messages");
    } finally {
      setLoading(false);
    }
  }, [searchPhone, directionFilter, token]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleSendSms = async () => {
    if (!sendTo.trim() || !sendMessage.trim()) {
      toast.error("Please enter phone number and message");
      return;
    }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("to", sendTo);
      formData.append("message", sendMessage);
      await axios.post(`${API_URL}/sms/send`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("SMS sent successfully!");
      setSendMessage("");
      setSendTo("");
      setShowSendModal(false);
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send SMS");
    } finally {
      setSending(false);
    }
  };

  // Group messages by phone number for conversation view
  const groupedMessages = messages.reduce((acc, msg) => {
    const phone = msg.phone_number;
    if (!acc[phone]) acc[phone] = [];
    acc[phone].push(msg);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-blue-600" />
              SMS Management
            </h1>
            <p className="text-muted-foreground">
              View and send SMS messages to drivers and contacts
            </p>
          </div>
          <Button
            onClick={() => setShowSendModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="new-sms-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Send New SMS
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by phone number..."
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  className="pl-10"
                  data-testid="sms-search-input"
                />
              </div>
            </div>
            <select
              value={directionFilter}
              onChange={(e) => setDirectionFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg bg-white"
              data-testid="sms-direction-filter"
            >
              <option value="">All Messages</option>
              <option value="inbound">Received Only</option>
              <option value="outbound">Sent Only</option>
            </select>
            <Button
              variant="outline"
              onClick={fetchMessages}
              disabled={loading}
              data-testid="sms-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Messages List */}
        <div className="bg-white rounded-lg shadow-sm">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-2" />
              <p className="text-muted-foreground">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-muted-foreground">No SMS messages found</p>
            </div>
          ) : (
            <div className="divide-y">
              {/* Show as conversation threads */}
              {Object.entries(groupedMessages).map(([phone, msgs]) => (
                <div key={phone} className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Phone className="h-4 w-4 text-blue-600" />
                    <span className="font-medium">{phone}</span>
                    <span className="text-xs text-muted-foreground">
                      ({msgs.length} messages)
                    </span>
                  </div>
                  <div className="space-y-2 pl-6">
                    {msgs.slice(0, 5).map((msg, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className={`p-3 rounded-lg text-sm ${
                          msg.direction === "outbound"
                            ? "bg-blue-50 border-l-4 border-blue-500 ml-8"
                            : "bg-gray-50 border-l-4 border-gray-400 mr-8"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="flex items-center gap-1 text-xs font-medium">
                            {msg.direction === "outbound" ? (
                              <>
                                <ArrowUpRight className="h-3 w-3 text-blue-600" />
                                <span className="text-blue-600">Sent</span>
                              </>
                            ) : (
                              <>
                                <ArrowDownLeft className="h-3 w-3 text-green-600" />
                                <span className="text-green-600">Received</span>
                              </>
                            )}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(msg.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-slate-700">{msg.body}</p>
                        {msg.message_type === "location_request" && (
                          <span className="inline-flex items-center gap-1 mt-2 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                            <MapPin className="h-3 w-3" />
                            Location Request
                          </span>
                        )}
                      </motion.div>
                    ))}
                    {msgs.length > 5 && (
                      <p className="text-xs text-muted-foreground text-center">
                        + {msgs.length - 5} more messages
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Send SMS Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
          >
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <Send className="h-5 w-5 text-blue-600" />
                Send SMS
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm font-medium">To (Phone Number)</label>
                <Input
                  value={sendTo}
                  onChange={(e) => setSendTo(e.target.value)}
                  placeholder="+386..."
                  className="mt-1"
                  data-testid="modal-sms-to"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Include country code (e.g., +386 for Slovenia)
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">Message</label>
                <textarea
                  value={sendMessage}
                  onChange={(e) => setSendMessage(e.target.value)}
                  placeholder="Enter your message..."
                  className="w-full mt-1 p-3 border rounded-lg min-h-[120px] text-sm"
                  data-testid="modal-sms-message"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  {sendMessage.length}/160 characters (1 SMS segment)
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowSendModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendSms}
                  disabled={sending || !sendTo.trim() || !sendMessage.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="modal-send-sms-btn"
                >
                  {sending ? "Sending..." : "Send SMS"}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
